import { useState, useEffect } from "react";
import { Layout } from "@/components/Layout";
import { AdminLayout } from "@/components/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  BarChart3,
  Users,
  Gamepad2,
  Building2,
  Key,
  MessageSquare,
  TrendingUp,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  Coins,
  Trophy,
  Heart,
  Star,
  AlertTriangle,
  CreditCard,
  Lightbulb,
  ShoppingBag,
  RefreshCw,
  Loader2
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Gems } from "@/components/Gems";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area
} from "recharts";

interface AdminStats {
  totalGames: number;
  totalCreators: number;
  totalDevelopers: number;
  totalPlayers: number;
  totalKeyRequests: number;
  approvedKeyRequests: number;
  rejectedKeyRequests: number;
  pendingKeyRequests: number;
  totalMessages: number;
  gamesByMonth: Array<{ month: string; count: number }>;
  keyRequestsByMonth: Array<{ month: string; approved: number; rejected: number; pending: number }>;
  messagesByMonth: Array<{ month: string; count: number }>;
  userRegistrationsByMonth: Array<{ month: string; creators: number; developers: number; players: number }>;
  revenueByMonth: Array<{ month: string; revenue: number }>;
  // New player stats
  totalCoinsCollected: number;
  totalMissionsDone: number;
  totalGamesLiked: number;
  totalGamesFavorited: number;
  // Admin personal stats
  adminCoinsCollected: number;
  adminMissionsDone: number;
  adminGamesLiked: number;
  adminGamesFavorited: number;
  // Payment stats
  totalSubscriptions: number;
  averageSpendingPerDeveloper: number;
  mostPurchasedProduct: string;
  totalRevenue: number;
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState("6months");
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (user?.id) {
      checkAdminAccess();
    }
  }, [user?.id]);

  useEffect(() => {
    if (isAdmin) {
      fetchStats();
    }
  }, [isAdmin, dateRange]);

  const checkAdminAccess = async () => {
    if (!user) return;

    try {
      const { data } = await supabase
        .from("profiles")
        .select("is_admin, display_name")
        .eq("id", user.id)
        .single();

      // Fallback to display_name check if is_admin column doesn't exist yet
      if (data?.is_admin || data?.display_name === 'miikeu') {
        setIsAdmin(true);
      } else {
        toast.error("Access denied. Admin privileges required.");
        navigate("/dashboard");
      }
    } catch (error) {
      console.error("Error checking admin access:", error);
      // Fallback: allow miikeu user even if there's a DB error
      if (user.email?.includes('miikeu') || user.user_metadata?.display_name === 'miikeu') {
        setIsAdmin(true);
      } else {
        toast.error("Error checking admin access");
        navigate("/dashboard");
      }
    }
  };

  const fetchStats = async () => {
    try {
      setLoading(true);

      // Calculate date range
      const now = new Date();
      let startDate: Date;

      switch (dateRange) {
        case "1month":
          startDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
          break;
        case "3months":
          startDate = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
          break;
        case "6months":
          startDate = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
          break;
        case "1year":
          startDate = new Date(now.getFullYear() - 1, now.getMonth(), now.getDate());
          break;
        default:
          startDate = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
      }

      // Fetch basic stats
      const [
        gamesResult,
        creatorsResult,
        developersResult,
        playersResult,
        keyRequestsResult,
        messagesResult,
        // New player stats
        coinsResult,
        missionsResult,
        gameLikesResult,
        gameFavoritesResult,
        // Admin personal stats
        adminCoinsResult,
        adminMissionsResult,
        adminGameLikesResult,
        adminGameFavoritesResult,
        // Payment stats - filtered by date for period-specific stats
        paymentsResult,
        // All paid payments for total revenue (no date filter)
        allPaidPaymentsResult
      ] = await Promise.all([
        supabase.from("games").select("id, created_at, is_published"),
        supabase.from("profiles").select("id, user_type, created_at").eq("user_type", "creator"),
        supabase.from("profiles").select("id, user_type, created_at").eq("user_type", "developer"),
        supabase.from("profiles").select("id, user_type, created_at").eq("user_type", "player"),
        supabase.from("key_requests").select("id, status, created_at"),
        supabase.from("messages").select("id, created_at"),
        // New player stats
        supabase.from("profiles").select("coins, id").eq("user_type", "player"),
        supabase.from("player_missions").select("id").limit(1000),
        supabase.from("game_likes").select("count", { count: 'exact' }),
        supabase.from("game_favorites").select("count", { count: 'exact' }),
        // Admin personal stats
        supabase.from("profiles").select("coins").eq("id", user.id).single(),
        supabase.from("player_missions").select("id", { count: 'exact' }).eq("player_id", user.id),
        supabase.from("game_likes").select("id", { count: 'exact' }).eq("player_id", user.id),
        supabase.from("game_favorites").select("id", { count: 'exact' }).eq("player_id", user.id),
        // Fetch payments within date range
        // For stats, we want payments created or paid within the date range
        supabase
          .from("payments")
          .select("id, user_id, amount, currency, status, payment_type, paid_at, created_at")
          .gte("created_at", startDate.toISOString()),
        // Fetch ALL payments for total revenue calculation (no date filter, no status filter)
        // Include user_id for average spending calculation
        supabase
          .from("payments")
          .select("id, user_id, amount, currency, status, payment_type, paid_at, created_at")
      ]);

      if (gamesResult.error) throw gamesResult.error;
      if (creatorsResult.error) throw creatorsResult.error;
      if (developersResult.error) throw developersResult.error;
      if (playersResult.error) throw playersResult.error;
      if (keyRequestsResult.error) throw keyRequestsResult.error;
      if (messagesResult.error) throw messagesResult.error;
      if (coinsResult.error) throw coinsResult.error;
      if (missionsResult.error) throw missionsResult.error;
      if (gameLikesResult.error) throw gameLikesResult.error;
      if (gameFavoritesResult.error) throw gameFavoritesResult.error;
      if (adminCoinsResult.error) throw adminCoinsResult.error;
      if (adminMissionsResult.error) throw adminMissionsResult.error;
      if (adminGameLikesResult.error) throw adminGameLikesResult.error;
      if (adminGameFavoritesResult.error) throw adminGameFavoritesResult.error;
      if (paymentsResult.error) {
        console.error("Error fetching payments (filtered):", paymentsResult.error);
        throw paymentsResult.error;
      }
      if (allPaidPaymentsResult.error) {
        console.error("Error fetching all paid payments:", allPaidPaymentsResult.error);
        throw allPaidPaymentsResult.error;
      }

      // Process data
      const totalGames = gamesResult.data?.filter(g => g.is_published).length || 0;
      const totalCreators = creatorsResult.data?.length || 0;
      const totalDevelopers = developersResult.data?.length || 0;
      const totalPlayers = playersResult.data?.length || 0;
      const totalKeyRequests = keyRequestsResult.data?.length || 0;
      const approvedKeyRequests = keyRequestsResult.data?.filter(k => k.status === "approved").length || 0;
      const rejectedKeyRequests = keyRequestsResult.data?.filter(k => k.status === "rejected").length || 0;
      const pendingKeyRequests = keyRequestsResult.data?.filter(k => k.status === "pending").length || 0;
      const totalMessages = messagesResult.data?.length || 0;

      // Calculate new player stats
      const totalCoinsCollected = coinsResult.data?.reduce((sum, player) => sum + (player.coins || 0), 0) || 0;
      const totalMissionsDone = missionsResult.data?.length || 0;
      const totalGamesLiked = gameLikesResult.count || 0;
      const totalGamesFavorited = gameFavoritesResult.count || 0;

      // Calculate admin personal stats
      const adminCoinsCollected = adminCoinsResult.data?.coins || 0;
      const adminMissionsDone = adminMissionsResult.count ?? adminMissionsResult.data?.length ?? 0;
      const adminGamesLiked = adminGameLikesResult.count ?? adminGameLikesResult.data?.length ?? 0;
      const adminGamesFavorited = adminGameFavoritesResult.count ?? adminGameFavoritesResult.data?.length ?? 0;

      // Calculate payment stats
      const payments = paymentsResult.data || [];
      // Use paid_at as indicator for paid payments (more reliable than status)
      const paidPayments = payments.filter(p => p.paid_at !== null && p.paid_at !== undefined);

      // Total subscriptions (premium_monthly + premium_yearly)
      const totalSubscriptions = paidPayments.filter(
        p => p.payment_type === "premium_monthly" || p.payment_type === "premium_yearly"
      ).length;

      // Total revenue (sum of PAID payments only)
      const allPayments = (allPaidPaymentsResult.data && Array.isArray(allPaidPaymentsResult.data))
        ? allPaidPaymentsResult.data
        : [];

      // Calculate total revenue from PAID payments only (filter by paid_at)
      const totalRevenue = allPayments
        .filter(p => p.paid_at !== null && p.paid_at !== undefined)
        .reduce((sum, p) => {
          // Handle DECIMAL/NUMERIC types from PostgreSQL which come as strings
          let amount = 0;
          if (p && p.amount !== null && p.amount !== undefined) {
            if (typeof p.amount === 'string') {
              amount = parseFloat(p.amount) || 0;
            } else if (typeof p.amount === 'number') {
              amount = p.amount;
            }
          }
          return sum + amount;
        }, 0);

      // Average spending per developer
      // Simply divide total revenue by total number of developers in profiles table
      const averageSpendingPerDeveloper = totalDevelopers > 0
        ? totalRevenue / totalDevelopers
        : 0;

      // Most purchased product (excluding premium subscriptions)
      // Use ALL payments (not just paid) for product popularity, but filter by date range
      const nonPremiumPayments = payments.filter(
        p => p.payment_type !== "premium_monthly" && p.payment_type !== "premium_yearly"
      );

      const productCounts: Record<string, number> = {};
      nonPremiumPayments.forEach(p => {
        const productType = p.payment_type;
        if (productType) {
          productCounts[productType] = (productCounts[productType] || 0) + 1;
        }
      });

      const mostPurchasedProduct = Object.keys(productCounts).length > 0
        ? Object.entries(productCounts).sort((a, b) => b[1] - a[1])[0][0]
        : "None";

      // Format product name for display
      const formatProductName = (productType: string): string => {
        if (productType === "None") return "None";
        const labels: Record<string, string> = {
          "press_kit": "Press Kit",
          "level2_boost": "Level 2 Boost",
          "level3_boost": "Level 3 Boost",
          "level4_boost": "Level 4 Boost",
          "twitter_post": "Twitter Post",
          "newsletter_basic": "Newsletter Basic",
          "creator_push": "Creator Push"
        };
        return labels[productType] || productType.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      };

      // Generate monthly data
      const gamesByMonth = generateMonthlyData(gamesResult.data?.filter(g => g.is_published) || [], startDate);
      const keyRequestsByMonth = generateKeyRequestsMonthlyData(keyRequestsResult.data || [], startDate);
      const messagesByMonth = generateMonthlyData(messagesResult.data || [], startDate);
      const userRegistrationsByMonth = generateUserRegistrationsMonthlyData([
        ...(creatorsResult.data || []),
        ...(developersResult.data || []),
        ...(playersResult.data || [])
      ], startDate);
      // Use all payments for revenue chart (not just in date range, regardless of status)
      const revenueByMonth = generateRevenueMonthlyData(allPayments, startDate);

      setStats({
        totalGames,
        totalCreators,
        totalDevelopers,
        totalPlayers,
        totalKeyRequests,
        approvedKeyRequests,
        rejectedKeyRequests,
        pendingKeyRequests,
        totalMessages,
        gamesByMonth,
        keyRequestsByMonth,
        messagesByMonth,
        userRegistrationsByMonth,
        revenueByMonth,
        // New player stats
        totalCoinsCollected,
        totalMissionsDone,
        totalGamesLiked,
        totalGamesFavorited,
        // Admin personal stats
        adminCoinsCollected,
        adminMissionsDone,
        adminGamesLiked,
        adminGamesFavorited,
        // Payment stats
        totalSubscriptions,
        averageSpendingPerDeveloper,
        mostPurchasedProduct: formatProductName(mostPurchasedProduct),
        totalRevenue
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
      toast.error("Failed to load statistics");
    } finally {
      setLoading(false);
    }
  };

  const generateMonthlyData = (data: any[], startDate: Date) => {
    const months: { [key: string]: number } = {};
    const currentDate = new Date();

    for (let d = new Date(startDate); d <= currentDate; d.setMonth(d.getMonth() + 1)) {
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      months[monthKey] = 0;
    }

    data.forEach(item => {
      const date = new Date(item.created_at);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (months.hasOwnProperty(monthKey)) {
        months[monthKey]++;
      }
    });

    return Object.entries(months).map(([month, count]) => ({
      month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
      count
    }));
  };

  const generateKeyRequestsMonthlyData = (data: any[], startDate: Date) => {
    const months: { [key: string]: { approved: number; rejected: number; pending: number } } = {};
    const currentDate = new Date();

    for (let d = new Date(startDate); d <= currentDate; d.setMonth(d.getMonth() + 1)) {
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      months[monthKey] = { approved: 0, rejected: 0, pending: 0 };
    }

    data.forEach(item => {
      const date = new Date(item.created_at);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (months.hasOwnProperty(monthKey)) {
        months[monthKey][item.status as keyof typeof months[typeof monthKey]]++;
      }
    });

    return Object.entries(months).map(([month, counts]) => ({
      month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
      ...counts
    }));
  };

  const generateUserRegistrationsMonthlyData = (data: any[], startDate: Date) => {
    const months: { [key: string]: { creators: number; developers: number; players: number } } = {};
    const currentDate = new Date();

    for (let d = new Date(startDate); d <= currentDate; d.setMonth(d.getMonth() + 1)) {
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      months[monthKey] = { creators: 0, developers: 0, players: 0 };
    }

    data.forEach(item => {
      const date = new Date(item.created_at);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (months.hasOwnProperty(monthKey)) {
        const userType = item.user_type;
        if (userType === 'creator') {
          months[monthKey].creators++;
        } else if (userType === 'developer') {
          months[monthKey].developers++;
        } else if (userType === 'player') {
          months[monthKey].players++;
        }
      }
    });

    return Object.entries(months).map(([month, counts]) => ({
      month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
      ...counts
    }));
  };

  const generateRevenueMonthlyData = (payments: any[], startDate: Date) => {
    const months: { [key: string]: number } = {};
    const currentDate = new Date();

    for (let d = new Date(startDate); d <= currentDate; d.setMonth(d.getMonth() + 1)) {
      const monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      months[monthKey] = 0;
    }

    payments.forEach(payment => {
      // Only include payments that have been paid (skip pending payments)
      if (payment.paid_at === null || payment.paid_at === undefined) {
        return;
      }

      // Use paid_at for the payment date
      const paymentDate = new Date(payment.paid_at);
      const monthKey = `${paymentDate.getFullYear()}-${String(paymentDate.getMonth() + 1).padStart(2, '0')}`;
      if (months.hasOwnProperty(monthKey)) {
        // Handle DECIMAL/NUMERIC types from PostgreSQL
        let amount = 0;
        if (payment.amount !== null && payment.amount !== undefined) {
          if (typeof payment.amount === 'string') {
            amount = parseFloat(payment.amount) || 0;
          } else if (typeof payment.amount === 'number') {
            amount = payment.amount;
          }
        }
        months[monthKey] += amount;
      }
    });

    return Object.entries(months).map(([month, revenue]) => ({
      month: new Date(month + '-01').toLocaleDateString('en-US', { month: 'short', year: 'numeric' }),
      revenue: Number(revenue.toFixed(2))
    }));
  };

  if (!isAdmin) {
    return (
      <AdminLayout>
        <div className="container mx-auto">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">Access Denied</h1>
            <p className="text-muted-foreground">You don't have permission to access this page.</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="container mx-auto">
          <div className="text-center py-20">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            <p className="mt-2 text-muted-foreground">Loading statistics...</p>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="container mx-auto">
        <div className="flex flex-col md:flex-row gap-4 items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold">Admin Dashboard</h1>
            <p className="text-muted-foreground">Platform statistics and analytics</p>
          </div>
          <div className="flex items-center gap-4">
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="1month">Last Month</SelectItem>
                <SelectItem value="3months">Last 3 Months</SelectItem>
                <SelectItem value="6months">Last 6 Months</SelectItem>
                <SelectItem value="1year">Last Year</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {stats && (
          <>
            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Games</CardTitle>
                  <Gamepad2 className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.totalGames}</div>
                  <p className="text-xs text-muted-foreground">Published games</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Users</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.totalCreators + stats.totalDevelopers + stats.totalPlayers}</div>
                  <p className="text-xs text-muted-foreground">
                    {stats.totalCreators} creators, {stats.totalDevelopers} developers, {stats.totalPlayers} players
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Key Requests</CardTitle>
                  <Key className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.totalKeyRequests}</div>
                  <div className="flex gap-2 mt-1">
                    <Badge variant="default" className="text-xs">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      {stats.approvedKeyRequests}
                    </Badge>
                    <Badge variant="destructive" className="text-xs">
                      <XCircle className="w-3 h-3 mr-1" />
                      {stats.rejectedKeyRequests}
                    </Badge>
                    <Badge variant="secondary" className="text-xs">
                      <Clock className="w-3 h-3 mr-1" />
                      {stats.pendingKeyRequests}
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Messages</CardTitle>
                  <MessageSquare className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.totalMessages}</div>
                  <p className="text-xs text-muted-foreground">Total messages sent</p>
                </CardContent>
              </Card>
            </div>

            {/* Player Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Gems Available</CardTitle>
                  <Gems className="h-4 w-4" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.totalCoinsCollected.toLocaleString()}</div>
                  <p className="text-xs text-muted-foreground">Gems currently available to all players</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Missions Done</CardTitle>
                  <Trophy className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.totalMissionsDone}</div>
                  <p className="text-xs text-muted-foreground">Total missions completed</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Games Liked</CardTitle>
                  <Heart className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.totalGamesLiked}</div>
                  <p className="text-xs text-muted-foreground">Total game likes</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Games Favorited</CardTitle>
                  <Star className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.totalGamesFavorited}</div>
                  <p className="text-xs text-muted-foreground">Total game favorites</p>
                </CardContent>
              </Card>
            </div>

            {/* Payment Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Subscriptions</CardTitle>
                  <CreditCard className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.totalSubscriptions}</div>
                  <p className="text-xs text-muted-foreground">Active premium subscriptions</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Avg Spending/Developer</CardTitle>
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    €{stats.averageSpendingPerDeveloper.toFixed(2)}
                  </div>
                  <p className="text-xs text-muted-foreground">Average per developer</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Most Purchased Product</CardTitle>
                  <Trophy className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats.mostPurchasedProduct}</div>
                  <p className="text-xs text-muted-foreground">Excluding premium subscriptions</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">€{stats.totalRevenue.toFixed(2)}</div>
                  <p className="text-xs text-muted-foreground">From Mollie payments</p>
                </CardContent>
              </Card>
            </div>


            {/* Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    Games Published Over Time
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={stats.gamesByMonth.slice(-6)}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip />
                        <Bar dataKey="count" fill="hsl(var(--primary))" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    Key Requests Status
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={stats.keyRequestsByMonth.slice(-6)}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip />
                        <Area type="monotone" dataKey="approved" stackId="1" stroke="#22c55e" fill="#22c55e" fillOpacity={0.6} />
                        <Area type="monotone" dataKey="rejected" stackId="1" stroke="#ef4444" fill="#ef4444" fillOpacity={0.6} />
                        <Area type="monotone" dataKey="pending" stackId="1" stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.6} />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* User Registrations and Messages */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    User Registrations Over Time
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={stats.userRegistrationsByMonth.slice(-6)}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip />
                        <Line type="monotone" dataKey="creators" stroke="#3b82f6" strokeWidth={2} />
                        <Line type="monotone" dataKey="developers" stroke="#8b5cf6" strokeWidth={2} />
                        <Line type="monotone" dataKey="players" stroke="#10b981" strokeWidth={2} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    Revenue Over Time
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={stats.revenueByMonth.slice(-6)}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip
                          formatter={(value: number) => `€${value.toFixed(2)}`}
                        />
                        <Area
                          type="monotone"
                          dataKey="revenue"
                          stroke="hsl(var(--primary))"
                          fill="hsl(var(--primary))"
                          fillOpacity={0.3}
                          strokeWidth={2}
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Key Requests Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Key className="w-5 h-5" />
                  Key Requests Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={[
                          { name: "Approved", value: stats.approvedKeyRequests, color: "#22c55e" },
                          { name: "Rejected", value: stats.rejectedKeyRequests, color: "#ef4444" },
                          { name: "Pending", value: stats.pendingKeyRequests, color: "#f59e0b" }
                        ]}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {[
                          { name: "Approved", value: stats.approvedKeyRequests, color: "#22c55e" },
                          { name: "Rejected", value: stats.rejectedKeyRequests, color: "#ef4444" },
                          { name: "Pending", value: stats.pendingKeyRequests, color: "#f59e0b" }
                        ].map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </AdminLayout>
  );
}
